import React, { useState, useEffect } from 'react';
import { X, Smartphone, Send, CheckCircle, Loader2 } from 'lucide-react';
import api from '../api/axios';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PAYMENT_METHODS = [
  { id: 'TELEBIRR', name: 'Telebirr', account: '0929008467', owner: 'Tirhas', color: 'bg-blue-600' },
  { id: 'CBE', name: 'Commercial Bank (CBE)', account: '1000082659653', owner: 'TIRHAS HADGU ZEKARIAS', color: 'bg-purple-800' },  
];

const DepositModal: React.FC<DepositModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState(PAYMENT_METHODS[0]);
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [loading, setLoading] = useState(false);

  // 🚀 NEW: The useEffect to lock background scrolling on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;

    const telebirrMatch = rawVal.match(/transaction number is\s+([A-Z0-9]+)/i);
    const cbeFtMatch = rawVal.match(/(FT[A-Z0-9]{8,14})/i);
    const cbeNameMatch = rawVal.match(/Dear\s+([a-zA-Z\s]+)\s+You\s+have/i); 

    if (cbeFtMatch) {
      setReferenceNo(cbeFtMatch[1].toUpperCase());
    } else if (cbeNameMatch) {
      setReferenceNo(cbeNameMatch[1].trim().toUpperCase());
    } else if (telebirrMatch) {
      setReferenceNo(telebirrMatch[1].replace(/\./g, '').trim().toUpperCase());
    } else {
      setReferenceNo(rawVal);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !referenceNo) return;
    
    setLoading(true);
    try {
      let extractedRef = referenceNo.trim().toUpperCase();

      const cbeFtMatch = extractedRef.match(/(FT[A-Z0-9]{8,14})/i);
      const cbeNameMatch = extractedRef.match(/Dear\s+([a-zA-Z\s]+)\s+You\s+have/i);
      const telebirrMatch = extractedRef.match(/transaction number is\s+([A-Z0-9]+)/i);

      if (cbeFtMatch) extractedRef = cbeFtMatch[1].toUpperCase();
      else if (cbeNameMatch) extractedRef = cbeNameMatch[1].trim().toUpperCase();
      else if (telebirrMatch) extractedRef = telebirrMatch[1].replace(/\./g, '').trim().toUpperCase();

      await api.post('/payments/claim-deposit', {
        userSubmittedTxnId: extractedRef,
        amount: parseFloat(amount) 
      });
      
      setStep(3); 
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || "Verification failed. Please ensure the reference is valid or retry in 10 seconds.";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setAmount('');
    setReferenceNo('');
    onClose();
    if (step === 3) onSuccess();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      {/* 🚀 FIXED: Replaced overflow-hidden with max-h-[95vh] overflow-y-auto to enable internal scrolling! */}
      <div className="bg-white rounded-[2.5rem] w-full max-w-lg max-h-[95vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        
        <div className="p-6 border-b flex justify-between items-center bg-gray-50/50 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-xl"><Smartphone size={20}/></div>
            <h3 className="font-black text-slate-800 uppercase tracking-tight">Deposit Funds</h3>
          </div>
          <button type="button" onClick={resetAndClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-slate-400 hover:text-slate-600">
            <X size={20}/>
          </button>
        </div>

        <div className="p-8">
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
              <p className="text-sm text-gray-500 font-medium">1. Select your preferred payment method:</p>
              <div className="grid gap-3">
                {PAYMENT_METHODS.map((m) => (
                  <button 
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedMethod(m)}
                    className={`p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${selectedMethod.id === m.id ? 'border-blue-500 bg-blue-50/30' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl ${m.color} flex items-center justify-center text-white font-bold text-xs shadow-inner`}>{m.id.substring(0,2)}</div>
                      <div className="text-left">
                        <div className="font-bold text-slate-800 text-sm">{m.name}</div>
                        <div className="text-[10px] text-gray-400 font-medium">Instant Verification Available</div>
                      </div>
                    </div>
                    {selectedMethod.id === m.id && <CheckCircle size={18} className="text-blue-500 fill-blue-50"/>}
                  </button>
                ))}
              </div>
              
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={resetAndClose} className="flex-1 py-4 text-sm font-bold text-gray-400 hover:text-slate-600 transition-colors">Cancel</button>
                <button type="button" onClick={() => setStep(2)} className="flex-[2] bg-slate-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg active:scale-95">Next Step</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="bg-blue-600 p-6 rounded-3xl text-white shadow-xl shadow-blue-600/20">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Instructions</p>
                <p className="text-sm font-medium leading-relaxed">Please transfer the amount to the following <b>{selectedMethod.name}</b> account:</p>
                <div className="mt-4 p-4 bg-white/10 rounded-2xl border border-white/20">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] uppercase font-bold opacity-70 tracking-wider">Account No</span>
                    <span className="font-black text-lg tabular-nums">{selectedMethod.account}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase font-bold opacity-70 tracking-wider">Beneficiary</span>
                    <span className="font-bold text-xs uppercase">{selectedMethod.owner}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase text-gray-400 mb-1.5 block ml-1">Amount You Deposited (ETB)</label>
                  <input required type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 500" className="w-full bg-gray-50 border-2 border-gray-100 p-4 rounded-2xl outline-none focus:border-blue-500 font-bold text-slate-900 placeholder:text-gray-300" />
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase text-gray-400 mb-1 block ml-1">Transaction Reference / SMS</label>
                  <div className="p-3 mb-2 rounded-xl bg-blue-50 border border-blue-100 text-[11px] font-semibold text-blue-700 leading-snug">
                    {selectedMethod.id === 'TELEBIRR' ? (
                      <span>💡 <b>Telebirr:</b> Paste the SMS message received from <b>127</b>.</span>
                    ) : (
                      <span>💡 <b>CBE:</b> Paste the entire transfer SMS you received from <b>CBE</b> so we can verify your name.</span>
                    )}
                  </div>
                  <input required value={referenceNo} onChange={handleReferenceChange} placeholder={selectedMethod.id === 'TELEBIRR' ? "Paste SMS from 127" : "Paste your full CBE SMS here"} className="w-full bg-gray-50 border-2 border-gray-100 p-4 rounded-2xl outline-none focus:border-blue-500 font-mono text-sm text-slate-900 placeholder:text-gray-400" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setStep(1)} className="flex-1 py-4 text-sm font-bold text-gray-400 hover:text-slate-600">Back</button>
                <button disabled={loading} type="submit" className="flex-[2] bg-blue-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 active:scale-95 transition-all disabled:opacity-50">
                  {loading ? <Loader2 className="animate-spin" size={20}/> : <><Send size={18}/> Verify & Claim</>}
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="py-10 text-center animate-in zoom-in-95 duration-300">
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"><CheckCircle size={40}/></div>
              <h4 className="text-2xl font-black text-slate-800 mb-2">Deposit Verified!</h4>
              <p className="text-sm text-gray-500 max-w-xs mx-auto leading-relaxed mb-8">Your payment was successfully verified. Your wallet balance has been updated instantly.</p>
              <button onClick={resetAndClose} className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all">Close & Refresh</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepositModal;